/* Tandoori AI assistant: chat dialog for the separate assistant service.
   History lives only in this page's memory; replies and product cards are built with textContent. */
if (!customElements.get('tandoori-assistant')) {
  customElements.define('tandoori-assistant', class extends HTMLElement {
    connectedCallback() {
      if (!this.initialized) this.setup();
      if (!this.endpoint) return;
      // Lets the theme's back-to-top button sit above the launcher instead of under it.
      document.documentElement.classList.add('tb-has-assistant');
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
      this.watchStickyCart();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = 0;
      clearTimeout(this.tuckTimer);
      this.stickyObserver?.disconnect();
      document.documentElement.classList.remove('tb-has-assistant', 'tb-ai-open');
      document.documentElement.style.removeProperty('--tb-ai-lift');
    }

    setup() {
      this.initialized = true;
      const local = ['127.0.0.1', 'localhost'].includes(location.hostname);
      const configured = this.dataset.endpoint || (local ? 'http://127.0.0.1:9293' : '');
      if (!configured) return; // Unconfigured assistant stays hidden outside local preview.
      try {
        const endpoint = new URL(configured);
        if (endpoint.protocol !== 'https:' && !(local && endpoint.origin === 'http://127.0.0.1:9293')) return;
        this.endpoint = endpoint.href.replace(/\/$/, '');
      } catch { return; }
      this.hidden = false;
      this.messages = [];
      this.launch = this.querySelector('[data-ai-open]');
      this.dialog = this.querySelector('dialog');
      this.body = this.querySelector('[data-ai-body]');
      this.form = this.querySelector('[data-ai-form]');
      this.input = this.querySelector('[data-ai-input]');
      this.send = this.querySelector('[data-ai-send]');
      this.status = this.querySelector('[data-ai-status]');
      this.log = this.querySelector('[data-ai-log]');
      this.offline = this.querySelector('[data-ai-offline]');
      this.prompts = [...this.querySelectorAll('[data-ai-prompt]')];
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.phone = window.matchMedia('(max-width: 749px)');
      this.lastY = window.scrollY;
      this.onScroll = () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = 0;
          const y = window.scrollY;
          this.launch.classList.toggle('tb-ai-launch--compact', y > 320);
          // On phones the launcher tucks away while the page is scrolled down, so it never covers card text
          // mid-read; it returns on scroll up, when scrolling pauses, or while it has keyboard focus.
          if (this.phone.matches && y > 320 && y > this.lastY + 6 && document.activeElement !== this.launch) {
            this.launch.classList.add('tb-ai-launch--tucked');
          } else if (y < this.lastY - 6 || y <= 320) {
            this.launch.classList.remove('tb-ai-launch--tucked');
          }
          this.lastY = y;
          clearTimeout(this.tuckTimer);
          this.tuckTimer = setTimeout(() => this.launch.classList.remove('tb-ai-launch--tucked'), 900);
        });
      };

      this.launch.addEventListener('click', () => this.open());
      this.querySelector('[data-ai-close]').addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('close', () => document.documentElement.classList.remove('tb-ai-open'));
      // Close on backdrop clicks, but not when a drag that began inside the panel ends outside it.
      this.dialog.addEventListener('pointerdown', (event) => { this.pressedBackdrop = this.isBackdrop(event); });
      this.dialog.addEventListener('click', (event) => {
        if (this.pressedBackdrop && this.isBackdrop(event)) this.dialog.close();
        this.pressedBackdrop = false;
      });
      this.prompts.forEach((button) => button.addEventListener('click', () => this.usePrompt(button.textContent.trim())));
      this.input.addEventListener('input', () => { this.resize(); this.sync(); });
      this.input.addEventListener('keydown', (event) => {
        // Enter sends; Shift+Enter adds a line. Never interrupt an input-method composition.
        if (event.key !== 'Enter' || event.shiftKey || event.isComposing || event.keyCode === 229) return;
        event.preventDefault();
        this.ask();
      });
      this.form.addEventListener('submit', (event) => { event.preventDefault(); this.ask(); });
    }

    isBackdrop(event) {
      if (event.target !== this.dialog) return false;
      const rect = this.dialog.getBoundingClientRect();
      return event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
    }

    // Keep the launcher and back-to-top button above the theme's sticky add-to-cart bar, if one is added.
    watchStickyCart() {
      const bar = document.querySelector('sticky-cart');
      if (!bar || this.stickyObserver) return;
      const update = () => {
        const lift = bar.classList.contains('is-visible') ? bar.offsetHeight : 0;
        document.documentElement.style.setProperty('--tb-ai-lift', `${lift}px`);
      };
      this.stickyObserver = new MutationObserver(update);
      this.stickyObserver.observe(bar, { attributes: true, attributeFilter: ['class'] });
      update();
    }

    open() {
      if (this.dialog.open) return;
      document.documentElement.classList.add('tb-ai-open');
      try { this.dialog.showModal(); } catch { document.documentElement.classList.remove('tb-ai-open'); return; }
      // Focus the message box straight away on devices with a keyboard; on touch, wait for a tap
      // so the on-screen keyboard does not cover the suggestions.
      if (!this.input.disabled && window.matchMedia('(pointer: fine)').matches) this.input.focus();
      if (!this.ready) this.check();
    }

    async check() {
      if (this.busy || this.checking) return;
      this.checking = true;
      this.setState('checking');
      this.status.textContent = this.dataset.thinking;
      let ready = false;
      try {
        const response = await fetch(`${this.endpoint}/health`, { signal: AbortSignal.timeout(5000), credentials: 'omit' });
        ready = response.ok && (await response.json()).ready === true;
      } catch { ready = false; }
      this.checking = false;
      this.ready = ready;
      this.setState(ready ? 'ready' : 'offline');
      this.status.textContent = this.dataset[ready ? 'ready' : 'offline'];
    }

    setState(state) {
      this.dataset.state = state;
      if (state !== 'checking') {
        const offline = state === 'offline';
        const inputHadFocus = document.activeElement === this.input;
        this.offline.hidden = !offline;
        this.input.disabled = offline;
        this.prompts.forEach((button) => { button.disabled = offline; });
        if (offline && inputHadFocus) this.offline.querySelector('a')?.focus({ preventScroll: true });
      }
      this.sync();
    }

    sync() {
      this.send.disabled = !this.ready || Boolean(this.busy) || !this.input.value.trim();
    }

    usePrompt(text) {
      if (this.busy || !text) return;
      this.input.value = text;
      this.resize();
      this.sync();
      if (this.ready) this.ask();
      else this.input.focus();
    }

    // Grow the message box with its content, up to the CSS max-height (about five lines).
    resize() {
      const input = this.input;
      input.style.height = 'auto';
      const style = getComputedStyle(input);
      const borders = parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth);
      const max = parseFloat(style.maxHeight) || Infinity;
      const height = input.scrollHeight + borders;
      input.style.height = `${Math.min(height, max)}px`;
      input.style.overflowY = height > max ? 'auto' : 'hidden';
    }

    // Show the newest message: its start when it is taller than the panel, otherwise the very end.
    scrollToMessage(node) {
      if (!node || !this.dialog.open) return;
      const body = this.body;
      const top = node.getBoundingClientRect().top - body.getBoundingClientRect().top + body.scrollTop;
      const target = node.offsetHeight > body.clientHeight - 32 ? top - 16 : body.scrollHeight;
      body.scrollTo({ top: target, behavior: this.reducedMotion.matches ? 'instant' : 'smooth' });
    }

    append(role, text) {
      const message = document.createElement('div');
      message.className = `tb-ai-message tb-ai-message--${role === 'user' ? 'user' : 'assistant'}`;
      const label = document.createElement('span');
      label.className = 'visually-hidden';
      label.textContent = `${role === 'user' ? this.dataset.you : this.dataset.name}: `;
      message.append(label, document.createTextNode(text));
      this.log.append(message);
      this.scrollToMessage(message);
      return message;
    }

    appendError() {
      const message = this.append('assistant', this.dataset.error);
      message.classList.add('tb-ai-message--error');
      if (this.dataset.humanUrl && this.dataset.human) {
        const link = document.createElement('a');
        link.className = 'tb-ai-message__link';
        link.href = this.dataset.humanUrl;
        link.textContent = this.dataset.human;
        message.append(link);
        this.scrollToMessage(message);
      }
      return message;
    }

    showTyping() {
      this.hideTyping();
      const typing = document.createElement('div');
      typing.className = 'tb-ai-message tb-ai-typing';
      typing.setAttribute('aria-hidden', 'true');
      typing.append(document.createElement('span'), document.createElement('span'), document.createElement('span'));
      this.typing = typing;
      this.log.append(typing);
      this.scrollToMessage(typing);
    }

    hideTyping() {
      this.typing?.remove();
      this.typing = null;
    }

    safeImage(source) {
      if (typeof source !== 'string' || !source) return '';
      try {
        const url = new URL(source.startsWith('//') ? `https:${source}` : source, location.origin);
        if (url.origin === location.origin || (url.protocol === 'https:' && ['cdn.shopify.com', 'wi9rgr-id.myshopify.com'].includes(url.hostname))) return url.href;
      } catch { /* A malformed cover must not hide the answer. */ }
      return '';
    }

    productCards(products) {
      const cards = document.createElement('div');
      cards.className = 'tb-ai-products';
      for (const product of products.slice(0, 3)) {
        if (!product || typeof product.url !== 'string' || !/^\/products\/[a-z0-9%_-]+\?variant=\d+$/i.test(product.url)) continue;
        const link = document.createElement('a');
        link.className = 'tb-ai-product';
        link.href = product.url;
        const cover = document.createElement('span');
        cover.className = 'tb-ai-product__cover';
        const imageUrl = this.safeImage(product.image);
        if (imageUrl) {
          const image = document.createElement('img');
          image.src = imageUrl; image.alt = ''; image.width = 56; image.height = 74; image.loading = 'lazy'; image.decoding = 'async';
          // A cover that fails to load falls back to the plain placeholder instead of a broken-image icon.
          image.addEventListener('error', () => image.remove(), { once: true });
          cover.append(image);
        }
        const info = document.createElement('span');
        info.className = 'tb-ai-product__info';
        const title = document.createElement('span');
        title.className = 'tb-ai-product__title';
        title.textContent = String(product.title ?? '').slice(0, 300);
        info.append(title);
        if (typeof product.money === 'string' && product.money) {
          const price = document.createElement('span');
          price.className = 'tb-ai-product__price';
          price.textContent = product.money;
          info.append(price);
        }
        const chevron = document.createElement('span');
        chevron.className = 'tb-ai-product__chevron';
        chevron.setAttribute('aria-hidden', 'true');
        chevron.textContent = '›';
        link.append(cover, info, chevron);
        cards.append(link);
      }
      return cards.childElementCount ? cards : null;
    }

    async ask() {
      const text = this.input.value.trim();
      if (!this.ready || this.busy || !text || text.length > 1600) return;
      // Return focus to the message box afterwards when typing continues there; on touch screens,
      // a tapped send button or suggestion should not reopen the on-screen keyboard over the answer.
      const keepFocus = document.activeElement === this.input || window.matchMedia('(pointer: fine)').matches;
      this.busy = true;
      this.prompts.forEach((button) => { button.disabled = true; });
      this.status.textContent = this.dataset.thinking;
      this.append('user', text);
      this.input.value = '';
      this.resize();
      this.sync();
      this.showTyping();
      // Bound both turns and character count; history stays only in this page's memory.
      const history = [...this.messages.slice(-4), { role: 'user', content: text }];
      while (history.reduce((n, m) => n + m.content.length, 0) > 8500) history.shift();
      try {
        const response = await fetch(`${this.endpoint}/chat`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'omit', signal: AbortSignal.timeout(65000),
          body: JSON.stringify({ messages: history, productPath: location.pathname.startsWith('/products/') ? location.pathname : '' }),
        });
        if (!response.ok) throw new Error('Unavailable');
        const result = await response.json();
        if (typeof result.answer !== 'string' || !Array.isArray(result.products)) throw new Error('Invalid answer');
        this.hideTyping();
        const answer = this.append('assistant', result.answer);
        this.messages = [...history, { role: 'assistant', content: result.answer.slice(0, 1600) }];
        const cards = this.productCards(result.products);
        if (cards) this.log.append(cards);
        this.status.textContent = '';
        this.scrollToMessage(answer);
      } catch {
        this.hideTyping();
        // The error bubble is announced through the log; keep the question so it can be resent.
        this.status.textContent = '';
        this.appendError();
        this.input.value = text;
        this.resize();
      } finally {
        this.busy = false;
        this.prompts.forEach((button) => { button.disabled = !this.ready; });
        this.sync();
        if (keepFocus && this.dialog.open && !this.input.disabled) this.input.focus({ preventScroll: true });
      }
    }
  });
}
